import { useEffect, useRef, useState } from "react";
import { useI18n } from "../lib/i18n";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Images, Loader2, Trash2, Search, Coins, Film, ImageIcon, X, Play, Pencil, Clapperboard, Download, Wand2, CheckSquare, Square, CheckCheck, FolderOpen } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import GenerateDialog from "../components/GenerateDialog";
import { isFsSupported, ensureFolder, connectFolder, saveToFolder } from "../lib/localFolder";

const L = {
  it: {
    title: "Gallery",
    sub: "Tutte le immagini e i video generati, collegati ai tuoi prompt e playlist.",
    all: "Tutte", images: "Immagini", videos: "Video",
    playlist: "Playlist", all_playlists: "Tutte le playlist",
    search_ph: "Cerca nel prompt…",
    empty: "Nessun elemento ancora. Genera la tua prima immagine o video dal Generatore.",
    delete: "Elimina", confirm_delete: "Eliminare questo elemento dalla gallery?",
    deleted: "Eliminato", credits_word: "crediti", credit_one: "credito", close: "Chiudi",
    saved_local: "Salvata in cartella locale", save_folder: "Salva nella cartella", save_failed: "Salvataggio nella cartella non riuscito", saving: "Salvataggio…", saved_vxf: "Salvata su Vixelforge", saved_drive: "Salvata su Google Drive", open_drive: "Apri su Drive", not_saved: "Solo anteprima", avail_temp: "Disponibile su Vixelforge (7 giorni)", edit: "Modifica",
    move_to: "Playlist", no_playlist: "Nessuna", moved: "Spostato", make_video: "Genera video", edit_prompt: "Modifica prompt", edit_studio: "Modifica IMG/VID", download: "Scarica", select: "Seleziona", cancel_sel: "Annulla", select_all: "Seleziona tutti", deselect_all: "Deseleziona tutti", n_selected: "{n} selezionati", delete_sel: "Elimina", move_sel: "Sposta in playlist…", confirm_bulk: "Eliminare {n} elementi dalla gallery? L'operazione non è reversibile.", bulk_deleted: "{n} elementi eliminati", bulk_moved: "{n} elementi spostati", zip_sel: "Scarica ZIP", zip_busy: "Preparo lo ZIP…", zip_skipped: "{n} elementi non salvati su Vixelforge (es. su Drive o nella cartella locale) non sono inclusi", zip_none: "Nessuno degli elementi selezionati è salvato su Vixelforge",
    trial_banner: "Prova gratuita: le immagini generate restano qui per 7 giorni. Scaricale o attiva un piano per conservarle tutte.", trial_cta: "Scopri i piani",
  },
  en: {
    title: "Gallery",
    sub: "Every generated image and video, linked to your prompts and playlists.",
    all: "All", images: "Images", videos: "Videos",
    playlist: "Playlist", all_playlists: "All playlists",
    search_ph: "Search in prompt…",
    empty: "Nothing yet. Generate your first image or video from the Generator.",
    delete: "Delete", confirm_delete: "Delete this item from the gallery?",
    deleted: "Deleted", credits_word: "credits", credit_one: "credit", close: "Close",
    saved_local: "Saved in local folder", save_folder: "Save to folder", save_failed: "Saving to folder failed", saving: "Saving…", saved_vxf: "Saved on Vixelforge", saved_drive: "Saved on Google Drive", open_drive: "Open in Drive", not_saved: "Preview only", avail_temp: "Available on Vixelforge (7 days)", edit: "Edit",
    move_to: "Playlist", no_playlist: "None", moved: "Moved", make_video: "Generate video", edit_prompt: "Edit prompt", edit_studio: "Edit IMG/VID", download: "Download", select: "Select", cancel_sel: "Cancel", select_all: "Select all", deselect_all: "Deselect all", n_selected: "{n} selected", delete_sel: "Delete", move_sel: "Move to playlist…", confirm_bulk: "Delete {n} items from the gallery? This cannot be undone.", bulk_deleted: "{n} items deleted", bulk_moved: "{n} items moved", zip_sel: "Download ZIP", zip_busy: "Preparing ZIP…", zip_skipped: "{n} items not stored on Vixelforge (e.g. on Drive or in your local folder) are not included", zip_none: "None of the selected items is stored on Vixelforge",
    trial_banner: "Free trial: generated images stay here for 7 days. Download them or activate a plan to keep them all.", trial_cta: "See plans",
  },
};

function GenThumb({ id, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let objUrl;
    (async () => {
      try {
        const res = await api.get(`/generation/${id}/thumbnail`, { responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        if (!revoked) setUrl(objUrl);
      } catch { /* no thumb */ void 0; }
    })();
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [id]);
  if (!url) return <div className="w-full h-full flex items-center justify-center text-zinc-700"><ImageIcon className="w-8 h-8" /></div>;
  return <img src={url} alt={alt} className="w-full h-full object-cover" />;
}

function VideoPlayer({ id }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revoked = false;
    let objUrl;
    (async () => {
      try {
        const res = await api.get(`/generation/${id}/download`, { responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        if (!revoked) setUrl(objUrl);
      } catch { setErr(true); }
    })();
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [id]);
  if (err) return <div className="text-zinc-500 text-sm py-10">Video non disponibile</div>;
  if (!url) return <Loader2 className="w-8 h-8 animate-spin text-zinc-600" />;
  return <video src={url} controls autoPlay className="w-full max-h-[55vh] object-contain" data-testid="gallery-video-player" />;
}

export default function Gallery() {
  const { lang } = useI18n();
  const tt = L[lang] || L.en;
  const { user } = useAuth();
  const isTrial = !user?.plan || user.plan === "pending";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [lists, setLists] = useState([]);
  const [type, setType] = useState("");
  const [listId, setListId] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [i2v, setI2v] = useState(null);
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const toggleSel = (id) => setSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const exitSel = () => { setSelMode(false); setSel(new Set()); };
  const bulk = async (action, list_id) => {
    const ids = [...sel];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(tt.confirm_bulk.replace("{n}", ids.length))) return;
    try {
      const { data } = await api.post("/generation/bulk", { ids, action, list_id });
      toast.success((action === "delete" ? tt.bulk_deleted : tt.bulk_moved).replace("{n}", data.count));
      exitSel(); load();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const [zipping, setZipping] = useState(false);
  const bulkZip = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    setZipping(true);
    try {
      const res = await api.post("/generation/bulk/zip", { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `vixelforge-gallery-${Date.now()}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      const skipped = Number(res.headers["x-skipped"] || 0);
      if (skipped > 0) toast.info(tt.zip_skipped.replace("{n}", skipped));
    } catch { toast.error(tt.zip_none); }
    finally { setZipping(false); }
  };
  const download = async (id) => {
    try {
      const { data } = await api.get(`/generation/${id}/download-link`);
      const a = document.createElement("a");
      a.href = data.url; a.download = data.filename; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const [savingLocal, setSavingLocal] = useState(false);
  // Click gesture: (re)grant the connected folder, then fetch the Vixelforge copy and write it there.
  const saveLocal = async (g) => {
    setSavingLocal(true);
    try {
      const h = (await ensureFolder(true)) || (await connectFolder());
      const blob = (await api.get(`/generation/${g.generation_id}/download`, { responseType: "blob", timeout: 180000 })).data;
      const ext = g.type === "video" ? "mp4" : ((blob.type.split("/")[1] || "png").replace("jpeg", "jpg"));
      const path = await saveToFolder(h, { filename: `${g.generation_id}.${ext}`, blob });
      await api.patch(`/generation/${g.generation_id}`, { saved: true, storage_mode: "local", local_path: path });
      toast.success(tt.saved_local);
      setActive((a) => a && a.generation_id === g.generation_id ? { ...a, storage_mode: "local", saved: true, local_path: path } : a);
      load();
    } catch { toast.error(tt.save_failed); }
    finally { setSavingLocal(false); }
  };
  const debounce = useRef(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const openId = params.get("open");
    if (!openId || !items.length) return;
    const it = items.find((x) => x.generation_id === openId);
    if (it) { setActive(it); params.delete("open"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, [items]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.type = type;
      if (listId) params.list_id = listId;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/generation", { params });
      setItems(data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  };

  const loadLists = async () => {
    try { const { data } = await api.get("/prompts/lists"); setLists(data); } catch { /* ignore */ void 0; }
  };

  useEffect(() => { loadLists(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(load, 250);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line
  }, [type, listId, q]);

  const del = async (id) => {
    if (!window.confirm(tt.confirm_delete)) return;
    try {
      await api.delete(`/generation/${id}`);
      setItems((prev) => prev.filter((x) => x.generation_id !== id));
      if (active?.generation_id === id) setActive(null);
      toast.success(tt.deleted);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const moveToList = async (genId, listId) => {
    try {
      await api.post(`/generation/${genId}/move-to-list`, { list_id: listId });
      toast.success(tt.moved);
      if (active?.generation_id === genId) setActive({ ...active, list_id: listId || null });
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const Tab = ({ value, label, Icon }) => (
    <button
      onClick={() => setType(value)}
      data-testid={`gallery-tab-${value || "all"}`}
      className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${
        type === value ? "bg-orange-500 text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"
      }`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </button>
  );

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="gallery-page">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2">
          <Images className="w-7 h-7 text-orange-400" /> {tt.title}
        </h1>
        <p className="text-zinc-400 mt-2 text-sm max-w-2xl">{tt.sub}</p>
      </div>
      {isTrial && (
        <div className="mb-6 rounded-xl border border-orange-500/25 bg-orange-500/[0.06] px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm text-zinc-300" data-testid="gallery-trial-banner">
          <span>{tt.trial_banner}</span>
          <Link to="/app/account" className="font-bold text-orange-300 hover:text-orange-200" data-testid="gallery-trial-cta">{tt.trial_cta}</Link>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Tab value="" label={tt.all} />
        <Tab value="image" label={tt.images} Icon={ImageIcon} />
        <Tab value="video" label={tt.videos} Icon={Film} />
        <select
          value={listId}
          onChange={(e) => setListId(e.target.value)}
          data-testid="gallery-playlist-select"
          className="rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500/50"
        >
          <option value="" className="bg-zinc-900">{tt.all_playlists}</option>
          {lists.map((l) => <option key={l.list_id} value={l.list_id} className="bg-zinc-900">{l.name}</option>)}
        </select>
        <button onClick={() => (selMode ? exitSel() : setSelMode(true))} data-testid="gallery-select-mode-btn"
          className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors ${selMode ? "bg-orange-500 text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
          <CheckSquare className="w-3.5 h-3.5" /> {selMode ? tt.cancel_sel : tt.select}
        </button>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tt.search_ph}
            data-testid="gallery-search"
            className="w-full rounded-full bg-white/5 border border-white/10 pl-9 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/50"
          />
        </div>
      </div>

      {selMode && (
        <div className="sticky top-2 z-20 mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-orange-500/30 bg-[#16161C]/95 backdrop-blur px-3 py-2.5" data-testid="gallery-bulk-bar">
          <span className="text-sm font-semibold text-orange-300 mr-1" data-testid="gallery-selected-count">{tt.n_selected.replace("{n}", sel.size)}</span>
          <button onClick={() => setSel(sel.size === items.length ? new Set() : new Set(items.map((i) => i.generation_id)))} data-testid="gallery-select-all-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" /> {sel.size === items.length && items.length ? tt.deselect_all : tt.select_all}
          </button>
          <select value="" disabled={!sel.size} onChange={(e) => { const v = e.target.value; if (v) bulk("move", v === "__none" ? "" : v); }} data-testid="gallery-bulk-move-select"
            className="text-xs rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-white disabled:opacity-40">
            <option value="" className="bg-zinc-900">{tt.move_sel}</option>
            <option value="__none" className="bg-zinc-900">{tt.no_playlist}</option>
            {lists.map((l) => <option key={l.list_id} value={l.list_id} className="bg-zinc-900">{l.name}</option>)}
          </select>
          <button onClick={bulkZip} disabled={!sel.size || zipping} data-testid="gallery-bulk-zip-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-40 inline-flex items-center gap-1">
            {zipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {zipping ? tt.zip_busy : tt.zip_sel}
          </button>
          <button onClick={() => bulk("delete")} disabled={!sel.size} data-testid="gallery-bulk-delete-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-red-500/15 text-red-300 hover:bg-red-500/25 disabled:opacity-40 inline-flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> {tt.delete_sel}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-zinc-600" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="gallery-empty">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-orange-500/10 border border-orange-500/25 flex items-center justify-center">
            <Images className="w-9 h-9 text-orange-400" strokeWidth={1.6} />
          </div>
          <div className="text-zinc-400 text-sm max-w-xs mx-auto">{tt.empty}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {items.map((it) => (
            <div key={it.generation_id} className="group relative rounded-xl overflow-hidden border border-white/10 bg-[#121218]" data-testid={`gallery-item-${it.generation_id}`}>
              <button onClick={() => (selMode ? toggleSel(it.generation_id) : setActive(it))} className={`relative block w-full aspect-square ${selMode && sel.has(it.generation_id) ? "ring-2 ring-inset ring-orange-500" : ""}`} data-testid={`gallery-open-${it.generation_id}`}>
                {selMode && (
                  <span className="absolute top-2 right-2 z-10 pointer-events-none" data-testid={`gallery-check-${it.generation_id}`}>
                    {sel.has(it.generation_id) ? <CheckSquare className="w-5 h-5 text-orange-400 fill-black/60" /> : <Square className="w-5 h-5 text-white/80" />}
                  </span>
                )}
                <GenThumb id={it.generation_id} alt={it.prompt_text} />
                {it.type === "video" && (
                  <>
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="p-2 rounded-full bg-[#0E0E12]/50"><Play className="w-7 h-7 text-white/90 fill-white/90" /></span>
                    </span>
                    {it.duration && <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-[#0E0E12]/70 text-white">{String(it.duration).replace("s", "")}s</span>}
                  </>
                )}
                {it.status === "processing" && (
                  <span className="absolute inset-0 flex items-center justify-center bg-[#0E0E12]/60 pointer-events-none">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
                  </span>
                )}
              </button>
              {(it.storage_mode === "drive" || it.storage_mode === "local") && (
                <span data-testid={`gallery-dest-${it.generation_id}`} className="absolute top-2 left-2 text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#0E0E12]/75 text-amber-200 border border-amber-500/30">
                  {it.storage_mode === "drive" ? "DRIVE" : "FOLDER"}
                </span>
              )}
              <div className="p-2.5">
                <div className="text-xs font-semibold truncate">{it.model_name}</div>
                <div className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5">
                  <Coins className="w-3 h-3" /> {it.credits_spent} {it.credits_spent === 1 ? tt.credit_one : tt.credits_word}
                </div>
                {it.list_names?.length > 0 && (
                  <div className="text-[10px] text-orange-300/80 truncate mt-0.5">{it.list_names.join(" · ")}</div>
                )}
              </div>
              {it.has_media && !selMode && (
                <button onClick={() => download(it.generation_id)} data-testid={`gallery-download-${it.generation_id}`} title={tt.download}
                  className="absolute top-2 right-10 p-1.5 rounded-md bg-[#0E0E12]/60 text-zinc-300 opacity-100 sm:opacity-0 group-hover:opacity-100 hover:text-orange-300 transition">
                  <Download className="w-3.5 h-3.5" />
                </button>
              )}
              {!selMode && <button
                onClick={() => del(it.generation_id)}
                data-testid={`gallery-delete-${it.generation_id}`}
                className="absolute top-2 right-2 p-1.5 rounded-md bg-[#0E0E12]/60 text-zinc-300 opacity-0 group-hover:opacity-100 hover:text-red-400 transition"
                title={tt.delete}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>}
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-[#0E0E12]/80 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="gallery-modal">
          <div className="bg-[#121218] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="text-sm font-bold">{active.model_name}</div>
              <button onClick={() => setActive(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="rounded-lg overflow-hidden border border-white/10 bg-[#0E0E12] flex items-center justify-center max-h-[55vh]">
                {active.type === "video"
                  ? <VideoPlayer id={active.generation_id} />
                  : <GenThumb id={active.generation_id} alt={active.prompt_text} />}
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className="px-2 py-1 rounded bg-white/5 text-zinc-300 flex items-center gap-1"><Coins className="w-3 h-3" /> {active.credits_spent} {active.credits_spent === 1 ? tt.credit_one : tt.credits_word}</span>
                <span className="px-2 py-1 rounded bg-white/5 text-zinc-300">{active.size}{active.resolution ? ` · ${active.resolution}` : ""}</span>
                <span data-testid="modal-storage-badge" className={`px-2 py-1 rounded ${active.storage_mode ? "bg-green-500/10 text-green-400" : active.has_media ? "bg-sky-500/10 text-sky-300" : "bg-white/5 text-zinc-400"}`}>
                  {active.storage_mode === "vixelforge" ? tt.saved_vxf : active.storage_mode === "drive" ? tt.saved_drive : active.storage_mode === "local" ? tt.saved_local : active.has_media ? tt.avail_temp : tt.not_saved}
                </span>
                {active.storage_mode === "drive" && active.drive_file_id && (
                  <a href={`https://drive.google.com/file/d/${active.drive_file_id}/view`} target="_blank" rel="noreferrer"
                    data-testid="modal-open-drive" className="px-2 py-1 rounded bg-orange-500/10 text-orange-300 hover:bg-orange-500/20">{tt.open_drive} ↗</a>
                )}
              </div>
              <div className="text-sm text-zinc-300 whitespace-pre-wrap bg-white/5 rounded-lg p-3 border border-white/10">{active.prompt_text}</div>
              <div className="flex items-center gap-2" data-testid="modal-playlist-row">
                <span className="text-xs text-zinc-400">{tt.move_to}:</span>
                <select value={active.list_id || ""} onChange={(e) => moveToList(active.generation_id, e.target.value)}
                  data-testid="modal-playlist-select"
                  className="rounded-lg bg-white/5 border border-white/10 px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/50">
                  <option value="" className="bg-zinc-900">{tt.no_playlist}</option>
                  {lists.map((l) => <option key={l.list_id} value={l.list_id} className="bg-zinc-900">{l.name}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => { const pid = active.prompt_id; navigate(`/app?${pid ? `edit=${pid}&` : ""}media=${active.generation_id}`); }}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="modal-edit-btn">
                  <Pencil className="w-4 h-4 mr-2" /> {tt.edit_prompt}
                </Button>
                {active.has_media && (
                  <Button onClick={() => navigate(`/app/studio?gen=${active.generation_id}`)} variant="outline"
                    className="border-orange-500/40 bg-transparent text-orange-300 hover:bg-orange-500/10" data-testid="modal-edit-studio-btn">
                    <Wand2 className="w-4 h-4 mr-2" /> {tt.edit_studio}
                  </Button>
                )}
                {active.has_media && !active.storage_mode && isFsSupported() && (
                  <Button onClick={() => saveLocal(active)} disabled={savingLocal} data-testid="modal-save-folder-btn"
                    className="bg-orange-500 hover:bg-orange-600 text-black font-bold">
                    {savingLocal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-2" />} {savingLocal ? tt.saving : tt.save_folder}
                  </Button>
                )}
                {active.has_media && (
                  <Button onClick={() => download(active.generation_id)} variant="outline" data-testid="modal-download-btn"
                    className="border-white/15 bg-transparent hover:bg-white/10">
                    <Download className="w-4 h-4 mr-2" /> {tt.download}
                  </Button>
                )}
                {active.type === "image" && active.has_media && (
                  <Button onClick={() => { setI2v(active); setActive(null); }} variant="outline"
                    className="border-orange-500/40 bg-transparent text-orange-300 hover:bg-orange-500/10" data-testid="modal-make-video-btn">
                    <Clapperboard className="w-4 h-4 mr-2" /> {tt.make_video}
                  </Button>
                )}
                <Button variant="outline" onClick={() => del(active.generation_id)}
                  className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10" data-testid="modal-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> {tt.delete}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    <GenerateDialog open={!!i2v} onOpenChange={(v) => { if (!v) { setI2v(null); load(); } }}
        promptText={i2v?.prompt_text || ""} promptId={i2v?.prompt_id || null} outputType="video" editablePrompt={true}
        initialStart={i2v ? { kind: "gallery", id: i2v.generation_id } : null} />
    </div>
  );
}
